import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Upload, CheckCircle2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { buildSkillBreadcrumb, type TaxonomyBreadcrumbNode } from '@/lib/taxonomy-breadcrumb';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { BehaviorDescriptionHint } from '@/components/BehaviorDescriptionHint';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * 「認定の確定・インポート」タブ (人・組織機能 増分② SC-04/SC-05、D-607/D-630/D-654)。
 * 旧 `/talent/certifications` (`TalentCertificationsPage.tsx`) を人材育成ダッシュボードへ移設
 * (docs/design/v05-talent-development-dashboard-detail.md §0-2)。
 *
 * ⚠️ 配下メンバーの認定 (draft/confirmed) を一覧し、確定・インポートを行う「管理面」であり、
 * ロースタータブ (D-11 の可視性ルールで表示範囲を絞る閲覧面とは異なり、可視性ルールを介さない、
 * D-646) とは目的が異なる。対象社員の一覧は `/talent/skills` (配下メンバー確認と同じスコープ判定、
 * skill:view) から取得し、各社員の draft を含む詳細は `GET /employees/:id/skills` を個別に呼んで
 * 組み立てる (増分②時点で「人×面」の一覧集計 API は新設されていないため)。
 *
 * 確定操作 (`POST /certifications/:id/confirm`) は `certification:manage:all_stores` 保有者にのみ、
 * インポート (`POST /certifications/import`) は `skill:manage` の全店舗スコープ保有者にのみボタンを出す。
 *
 * v0.5 人材育成ダッシュボード §1-2: 移設にあわせ、レベル表示の隣に「?」アイコンで行動記述を表示する。
 * `GET /employees/:id/skills` のレスポンスに `behavior_description` が追加された (実装側連絡)。
 */

type TalentEmployee = { employee_id: string; display_name: string; home_node_id: string; home_node_name: string };
type EmployeeSkillItem = {
  skill_id: string; skill_level_id: string; skill_level_label: string | null;
  behavior_description: string | null;
  confirmation_status: 'draft' | 'confirmed'; employee_skill_id: string;
};
type EmployeeSkillsDetail = { employee_id: string; skills: EmployeeSkillItem[] };
type SkillMaster = { skill_id: string; label: string };
type SkillLevelMaster = { skill_level_id: string; label: string; sort_order: number };


type ConfirmTarget = { employeeSkillId: string; employeeName: string; skillLabel: string };
type ImportRow = { employeeId: string; skillId: string; skillLevelId: string };

export function CertificationsTab() {
  const user = useAuthStore((s) => s.user);
  const canConfirm = hasTenantWidePermission(user?.effective_permissions, 'certification', 'manage');
  const canImport = hasTenantWidePermission(user?.effective_permissions, 'skill', 'manage');
  const qc = useQueryClient();

  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget | null>(null);
  const [confirmNote, setConfirmNote] = useState('');
  const [confirmNoteError, setConfirmNoteError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);

  const { data: talentData, isLoading: employeesLoading, isError, error } = useQuery<{ employees: TalentEmployee[] }>({
    queryKey: ['talent-skills-for-certifications'],
    queryFn: () => apiFetch('/talent/skills'),
    retry: false,
  });
  const employees = talentData?.employees ?? [];

  const { data: skills = [] } = useQuery<SkillMaster[]>({ queryKey: ['skills'], queryFn: () => apiFetch('/masters/skills') });
  const { data: skillLevelsMaster = [] } = useQuery<SkillLevelMaster[]>({ queryKey: ['skill-levels'], queryFn: () => apiFetch('/masters/skill-levels') });
  const { data: skillTaxonomy } = useQuery<{ nodes: TaxonomyBreadcrumbNode[] }>({ queryKey: ['competency-taxonomy', 'skill'], queryFn: () => apiFetch('/masters/taxonomy/skill') });
  const skillLabel = (id: string) => skills.find((s) => s.skill_id === id)?.label ?? id;
  const skillBreadcrumb = (id: string) => buildSkillBreadcrumb(skillTaxonomy?.nodes ?? [], id);

  const detailQueries = useQueries({
    queries: employees.map((emp) => ({
      queryKey: ['employee-skills-detail', emp.employee_id],
      queryFn: () => apiFetch<EmployeeSkillsDetail>(`/employees/${emp.employee_id}/skills`),
    })),
  });
  const detailsLoading = employees.length > 0 && detailQueries.some((q) => q.isLoading);
  const detailByEmployee = new Map<string, EmployeeSkillItem[]>();
  employees.forEach((emp, i) => {
    const q = detailQueries[i];
    if (q?.data) detailByEmployee.set(emp.employee_id, q.data.skills);
  });

  const invalidateAllDetails = () => employees.forEach((emp) => qc.invalidateQueries({ queryKey: ['employee-skills-detail', emp.employee_id] }));

  const confirmMut = useMutation({
    mutationFn: ({ employeeSkillId, note }: { employeeSkillId: string; note: string }) =>
      apiFetch(`/certifications/${employeeSkillId}/confirm`, { method: 'POST', body: JSON.stringify({ note: note.trim() || undefined }) }),
    onSuccess: () => {
      invalidateAllDetails();
      setConfirmTarget(null); setConfirmNote(''); setConfirmNoteError(null);
      toast.success('確定しました');
    },
    onError: (e) => {
      // §5 API契約: calibration モード (目線合わせ会議) では note が必須。details は
      // ApiClientError に載らないため、メッセージ本文で判定する (throwValidation の一意な文言)。
      if (e instanceof ApiClientError && e.status === 400 && e.message.includes('note')) {
        setConfirmNoteError(e.message);
        return;
      }
      toast.error(e instanceof ApiClientError ? e.message : '確定に失敗しました');
    },
  });

  const importMut = useMutation({
    mutationFn: () => apiFetch<{ imported: number }>('/certifications/import', {
      method: 'POST',
      body: JSON.stringify({ rows: importRows.map((r) => ({ employee_id: r.employeeId, skill_id: r.skillId, skill_level_id: r.skillLevelId })) }),
    }),
    onSuccess: (res) => {
      invalidateAllDetails();
      setImportOpen(false); setImportRows([]);
      toast.success(`${res.imported}件を取り込みました`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'インポートに失敗しました'),
  });

  const openConfirm = (employeeName: string, item: EmployeeSkillItem) => {
    setConfirmTarget({ employeeSkillId: item.employee_skill_id, employeeName, skillLabel: skillLabel(item.skill_id) });
    setConfirmNote(''); setConfirmNoteError(null);
  };

  const addImportRow = () => {
    if (employees.length === 0 || skills.length === 0 || skillLevelsMaster.length === 0) return;
    setImportRows([...importRows, { employeeId: employees[0]!.employee_id, skillId: skills[0]!.skill_id, skillLevelId: skillLevelsMaster[0]!.skill_level_id }]);
  };
  const updateImportRow = (i: number, patch: Partial<ImportRow>) => setImportRows(importRows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeImportRow = (i: number) => setImportRows(importRows.filter((_, idx) => idx !== i));

  // ≥768px マトリクスの列 = 少なくとも1人が保有するスキルの集合 (D-647)
  const allSkillIds = [...new Set([...detailByEmployee.values()].flatMap((items) => items.map((it) => it.skill_id)))]
    .sort((a, b) => skillLabel(a).localeCompare(skillLabel(b), 'ja'));

  const statusBadge = (status: 'draft' | 'confirmed') => (
    status === 'draft'
      ? <span className="text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border whitespace-nowrap">未確定</span>
      : <span className="text-[11px] px-1.5 py-0.5 rounded bg-success-bg text-success-text border border-success-border whitespace-nowrap">確定済み</span>
  );

  const levelWithBehavior = (item: EmployeeSkillItem) => (
    <span className="inline-flex items-center gap-1">
      <span>{item.skill_level_label ?? '—'}</span>
      {item.behavior_description && <BehaviorDescriptionHint text={item.behavior_description} />}
    </span>
  );

  const body = () => {
    if (employeesLoading) return <div role="status" className="text-text-sub">読み込み中…</div>;
    if (isError) {
      const outOfScope = error instanceof ApiClientError && error.code === 'OUT_OF_SCOPE';
      return (
        <p role="alert" className="text-sm text-alert-text">
          {outOfScope ? '配下メンバーの認定を閲覧する権限がありません。' : '配下メンバーの認定の取得に失敗しました。'}
        </p>
      );
    }
    if (employees.length === 0) return <EmptyState message="配下メンバーが見つかりませんでした" />;

    return (
      <>
        {detailsLoading && <p className="text-xs text-text-sub" aria-live="polite">認定情報を読み込み中…</p>}

        {/* ≥768px: 人×スキルのマトリクス (D-647) */}
        <div className="hidden md:block border border-surface-border rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="border-collapse text-sm">
              <thead>
                <tr>
                  <th scope="col" className="sticky left-0 z-10 bg-surface-sunken border-b border-r border-surface-border px-3 py-2 text-left text-xs text-text-sub" style={{ minWidth: 160 }}>
                    対象者
                  </th>
                  {allSkillIds.map((skillId) => {
                    const { immediateParent, fullPath } = skillBreadcrumb(skillId);
                    return (
                      <th key={skillId} scope="col" aria-label={fullPath || skillLabel(skillId)}
                        className="relative border-b border-surface-border px-2 py-2 text-center text-xs text-text-sub whitespace-nowrap" style={{ minWidth: 140 }}>
                        {immediateParent && <div className="text-[10px] text-text-sub">{immediateParent}</div>}
                        {skillLabel(skillId)}
                      </th>
                    );
                  })}
                  {allSkillIds.length === 0 && (
                    <th scope="col" className="relative border-b border-surface-border px-3 py-2 text-center text-xs text-text-sub">認定データがまだありません</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => {
                  const items = detailByEmployee.get(emp.employee_id) ?? [];
                  const byskill = new Map(items.map((it) => [it.skill_id, it]));
                  return (
                    <tr key={emp.employee_id} className="odd:bg-white even:bg-surface-stripe">
                      <th scope="row" className="sticky left-0 z-10 bg-inherit border-r border-surface-border px-3 py-2 text-left align-top">
                        <div className="text-xs font-semibold text-text-body break-words">{emp.display_name}</div>
                        <div className="text-[11px] text-text-sub break-words">{emp.home_node_name}</div>
                      </th>
                      {allSkillIds.map((skillId) => {
                        const item = byskill.get(skillId);
                        return (
                          <td key={skillId} className="px-2 py-2 text-center align-top">
                            {!item ? (
                              <span className="text-xs text-text-sub">—</span>
                            ) : (
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-xs text-text-body whitespace-nowrap">{levelWithBehavior(item)}</span>
                                {statusBadge(item.confirmation_status)}
                                {item.confirmation_status === 'draft' && canConfirm && (
                                  <button type="button" onClick={() => openConfirm(emp.display_name, item)}
                                    className="inline-flex items-center gap-1 text-[11px] text-misespark-navy underline underline-offset-2 min-h-[44px] sm:min-h-0">
                                    <CheckCircle2 size={12} aria-hidden />確定
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* <768px: 対象者を選択してドリルダウン (D-647) */}
        <div className="md:hidden space-y-3">
          <div className="max-w-xs">
            <label className="block text-xs text-text-sub mb-1">対象者</label>
            <Select value={selectedEmployeeId || '__none__'} onValueChange={(v) => setSelectedEmployeeId(v === '__none__' ? '' : v)}>
              <SelectTrigger aria-label="対象者"><SelectValue placeholder="選択してください" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">選択してください</SelectItem>
                {employees.map((emp) => <SelectItem key={emp.employee_id} value={emp.employee_id}>{emp.display_name} ({emp.home_node_name})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!selectedEmployeeId ? (
            <p className="text-sm text-text-sub">上の「対象者」からまず選択してください。</p>
          ) : (
            (() => {
              const items = detailByEmployee.get(selectedEmployeeId) ?? [];
              const emp = employees.find((e) => e.employee_id === selectedEmployeeId)!;
              if (items.length === 0) return <EmptyState message="この対象者の認定はまだありません" />;
              return (
                <ul className="space-y-2">
                  {items.map((item) => {
                    const { immediateParent, fullPath } = skillBreadcrumb(item.skill_id);
                    return (
                    <li key={item.skill_id} className="border border-surface-border rounded-lg bg-white p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span aria-label={fullPath || skillLabel(item.skill_id)} className="text-sm font-semibold text-text-body break-words">
                              {immediateParent && <span className="text-text-sub font-normal">{immediateParent} › </span>}
                              {skillLabel(item.skill_id)}
                            </span>
                            {statusBadge(item.confirmation_status)}
                          </div>
                          <p className="text-xs text-text-sub mt-1">{levelWithBehavior(item)}</p>
                        </div>
                        {item.confirmation_status === 'draft' && canConfirm && (
                          <Button variant="secondary" size="sm" leftIcon={<CheckCircle2 size={14} />} onClick={() => openConfirm(emp.display_name, item)}>
                            確定
                          </Button>
                        )}
                      </div>
                    </li>
                    );
                  })}
                </ul>
              );
            })()
          )}
        </div>
      </>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <p className="text-sm text-text-sub max-w-2xl">
          配下メンバーの人材像・スキルの認定を確認します。認定は draft (未確定) で作成され、確定操作で confirmed になります。
        </p>
        {canImport && (
          <Button variant="secondary" size="sm" leftIcon={<Upload size={14} />} onClick={() => setImportOpen(true)}>
            まとめて取り込む
          </Button>
        )}
      </div>

      {body()}

      <Modal open={!!confirmTarget} title="認定を確定" onClose={() => setConfirmTarget(null)} size="sm"
        footer={confirmTarget && (
          <>
            <Button variant="secondary" onClick={() => setConfirmTarget(null)}>キャンセル</Button>
            <Button variant="primary" loading={confirmMut.isPending}
              disabled={confirmMut.isPending || (!!confirmNoteError && confirmNote.trim() === '')}
              onClick={() => confirmMut.mutate({ employeeSkillId: confirmTarget.employeeSkillId, note: confirmNote })}>
              確定する
            </Button>
          </>
        )}>
        {confirmTarget && (
          <div className="space-y-3">
            <p className="text-sm text-text-body">
              {confirmTarget.employeeName} さんの「{confirmTarget.skillLabel}」を確定済みにします。
            </p>
            {confirmNoteError && <p className="text-xs text-alert-text">{confirmNoteError}</p>}
            <div>
              <label className="block text-sm text-text-sub mb-1">
                メモ{confirmNoteError ? ' (必須)' : ' (任意)'}
              </label>
              <textarea value={confirmNote} onChange={(e) => setConfirmNote(e.target.value)} maxLength={500} rows={3}
                placeholder="目線合わせ会議での決定事項など"
                className="w-full px-3 py-2 rounded border border-surface-border focus-ring" />
            </div>
          </div>
        )}
      </Modal>

      <Modal open={importOpen} title="認定をまとめて取り込む" onClose={() => setImportOpen(false)} size="lg"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setImportOpen(false)}>キャンセル</Button>
            <Button variant="primary" disabled={importRows.length === 0 || importMut.isPending} loading={importMut.isPending}
              onClick={() => importMut.mutate()}>
              インポート実行 ({importRows.length}件)
            </Button>
          </>
        )}>
        <div className="space-y-3">
          <p className="text-xs text-text-sub">
            最大500件まで一括で取り込めます。1件でも不正な行があると全件が取り消されます (部分的な反映はされません)。
          </p>
          {employees.length === 0 || skills.length === 0 || skillLevelsMaster.length === 0 ? (
            <p className="text-sm text-text-sub">対象者・スキル・習熟度のいずれかが未登録のため、インポートできません。</p>
          ) : (
            <>
              <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addImportRow}>行を追加</Button>
              {importRows.length === 0 ? (
                <p className="text-xs text-text-sub">行がまだありません。「行を追加」から登録してください。</p>
              ) : (
                <div className="space-y-2">
                  {importRows.map((row, i) => (
                    <div key={i} className="flex items-center gap-2 flex-wrap border border-surface-line rounded p-2">
                      <div className="flex-1 min-w-[160px]">
                        <Select value={row.employeeId} onValueChange={(v) => updateImportRow(i, { employeeId: v })}>
                          <SelectTrigger aria-label="対象者"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {employees.map((emp) => <SelectItem key={emp.employee_id} value={emp.employee_id}>{emp.display_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1 min-w-[140px]">
                        <Select value={row.skillId} onValueChange={(v) => updateImportRow(i, { skillId: v })}>
                          <SelectTrigger aria-label="スキル"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {skills.map((s) => <SelectItem key={s.skill_id} value={s.skill_id}>{s.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <Select value={row.skillLevelId} onValueChange={(v) => updateImportRow(i, { skillLevelId: v })}>
                          <SelectTrigger aria-label="習熟度"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {skillLevelsMaster.map((l) => <SelectItem key={l.skill_level_id} value={l.skill_level_id}>{l.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <button type="button" aria-label="この行を削除" onClick={() => removeImportRow(i)}
                        className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
